import {ChangeDetectionStrategy, Component, computed, ElementRef, input, output, signal, viewChild, ViewEncapsulation} from '@angular/core';
import {COUNTER_FROM, MAX_MESSAGE_LENGTH, STRINGS} from './assistant.constants';
import {IconComponent} from './icon.component';

const MAX_ROWS = 6;

/**
 * Auto-growing textarea (1-6 rows). Enter sends, Shift+Enter adds a line, and nothing sends while
 * an IME composition is active. The counter appears at 1,800 characters (hard limit 2,000) and is
 * announced at 90% and 100%. While an answer streams, Send becomes Stop and typing still works.
 */
@Component({
  selector: 'app-aa-composer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Global styles scoped under .aa-root, like the panel.
  encapsulation: ViewEncapsulation.None,
  styleUrl: './composer.scss',
  imports: [IconComponent],
  host: {'class': 'aa-composer-wrap'},
  template: `
    <div class="aa-composer">
      <label [for]="inputId()" class="aa-visually-hidden">{{ strings.composerLabel }}</label>
      <textarea #box
                [id]="inputId()"
                rows="1"
                [attr.maxlength]="max"
                [attr.aria-describedby]="showCounter() ? inputId() + '-counter' : null"
                [placeholder]="strings.placeholder"
                [value]="value()"
                (input)="onInput()"
                (keydown)="onKeydown($event)"></textarea>
      @if (showCounter()) {
        <span class="aa-counter aa-warn" [id]="inputId() + '-counter'">{{ counterText() }}</span>
      }
      @if (busy()) {
        <button class="aa-send" type="button" [attr.aria-label]="strings.stop" [title]="strings.stop" (click)="stop.emit()">
          <app-aa-icon name="square"/>
        </button>
      } @else {
        <button class="aa-send" type="button" [attr.aria-label]="strings.send" [title]="strings.send" [disabled]="!canSend()" (click)="submit()">
          <app-aa-icon name="arrow-up"/>
        </button>
      }
    </div>
    <p class="aa-disclaimer">{{ strings.disclaimerBefore }}<a href="https://assessor.lacounty.gov" target="_blank" rel="noopener noreferrer">assessor.lacounty.gov<span class="aa-visually-hidden"> {{ strings.opensInNewTab }}</span></a>.</p>
    <div class="aa-visually-hidden" aria-live="polite">{{ limitAnnouncement() }}</div>
  `,
})
export class ComposerComponent {
  readonly busy = input(false);
  readonly inputId = input('aa-composer-input');

  readonly send = output<string>();
  readonly stop = output<void>();

  protected readonly strings = STRINGS;
  protected readonly max = MAX_MESSAGE_LENGTH;
  protected readonly value = signal('');
  protected readonly limitAnnouncement = signal('');
  protected readonly showCounter = computed(() => this.value().length >= COUNTER_FROM);
  protected readonly canSend = computed(() => this.value().trim().length > 0 && this.value().length <= MAX_MESSAGE_LENGTH);
  protected readonly counterText = computed(() =>
    `${this.value().length.toLocaleString('en-US')} / ${MAX_MESSAGE_LENGTH.toLocaleString('en-US')}`);

  private readonly box = viewChild.required<ElementRef<HTMLTextAreaElement>>('box');
  private announcedLevel: 0 | 90 | 100 = 0;

  focus(): void {
    this.box().nativeElement.focus();
  }

  protected onInput(): void {
    const element = this.box().nativeElement;
    this.value.set(element.value);
    this.grow(element);
    this.announceLimit(element.value.length);
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }
    // IME: Enter confirms the composition, it must not send (keyCode 229 covers older Safari).
    if (event.isComposing || event.keyCode === 229) {
      return;
    }

    event.preventDefault();
    if (!this.busy()) {
      this.submit();
    }
  }

  protected submit(): void {
    if (!this.canSend()) {
      return;
    }

    this.send.emit(this.value().trim());
    this.value.set('');
    const element = this.box().nativeElement;
    element.value = '';
    this.grow(element);
    this.announcedLevel = 0;
    this.limitAnnouncement.set('');
    element.focus();
  }

  private grow(element: HTMLTextAreaElement): void {
    const style = getComputedStyle(element);
    const lineHeight = parseFloat(style.lineHeight) || 22;
    const padding = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, lineHeight * MAX_ROWS + padding)}px`;
  }

  private announceLimit(length: number): void {
    const level = length >= MAX_MESSAGE_LENGTH ? 100 : length >= COUNTER_FROM ? 90 : 0;
    if (level === this.announcedLevel) {
      return;
    }
    this.announcedLevel = level;
    this.limitAnnouncement.set(level === 100
      ? STRINGS.limitReached
      : level === 90 ? STRINGS.charactersLeft(MAX_MESSAGE_LENGTH - length) : '');
  }
}
